import { z } from "zod";
import {
  ARTICLE_STATUSES,
  PRODUCT_CONTENT_STATUSES,
  PRODUCT_CONTENT_TYPES,
  PRODUCT_STATUSES,
  PRODUCT_TYPES,
} from "../constants/domain";
import { VALIDATION_MESSAGES } from "../content/validation-messages";
import { parseYouTubeVideoId } from "../utils/youtube";
import { paginationQuerySchema } from "./pagination";

const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
const slug = z.string().trim().min(3).max(160).regex(SLUG_PATTERN, VALIDATION_MESSAGES.slugFormat);
const optionalText = (max: number) => z.string().trim().max(max).nullable().optional();
const rupiah = z.number().int().min(0).max(1_000_000_000);
const tagList = z.array(z.string().trim().min(1).max(60)).max(30);
const youTubeUrl = z
  .string()
  .trim()
  .max(300)
  .refine((link) => parseYouTubeVideoId(link) !== null, VALIDATION_MESSAGES.youTubeUrl);

export const productInputSchema = z
  .object({
    slug,
    title: z.string().trim().min(3).max(200),
    type: z.enum(PRODUCT_TYPES),
    status: z.enum(PRODUCT_STATUSES),
    shortDescription: optionalText(500),
    descriptionHtml: optionalText(100_000),
    imageKey: optionalText(500),
    teaserVideoUrl: youTubeUrl.nullable().optional(),
    authorId: z.uuid(VALIDATION_MESSAGES.authorRequired),
    normalPrice: rupiah,
    specialPrice: rupiah.nullable().optional(),
    benefits: z.array(z.string().trim().min(1).max(200)).max(20).default([]),
    tags: tagList.default([]),
    isHighlighted: z.boolean().default(false),
    seoTitle: optionalText(200),
    seoDescription: optionalText(320),
    seoKeywords: optionalText(500),
  })
  .refine((input) => input.specialPrice == null || input.specialPrice < input.normalPrice, {
    message: VALIDATION_MESSAGES.specialPriceBelowNormal,
    path: ["specialPrice"],
  });
export type ProductInput = z.infer<typeof productInputSchema>;

export const reorderSchema = z.object({ ids: z.array(z.uuid()).min(1).max(500) });

export const productContentInputSchema = z
  .object({
    type: z.enum(PRODUCT_CONTENT_TYPES),
    status: z.enum(PRODUCT_CONTENT_STATUSES).default("active"),
    title: z.string().trim().min(2).max(200),
    description: optionalText(2000),
    storageKey: optionalText(1000),
    externalUrl: z.url().max(2000).nullable().optional(),
  })
  .refine((input) => (input.type === "link" ? Boolean(input.externalUrl) : Boolean(input.storageKey)), {
    message: VALIDATION_MESSAGES.contentSourceRequired,
    path: ["storageKey"],
  });
export type ProductContentInput = z.infer<typeof productContentInputSchema>;

export const articleInputSchema = z.object({
  slug: z.string().trim().min(3).max(200).regex(SLUG_PATTERN, VALIDATION_MESSAGES.slugFormat),
  title: z.string().trim().min(3).max(300),
  excerpt: optionalText(1000),
  contentHtml: z.string().min(1).max(500_000),
  featuredImageKey: optionalText(500),
  featuredImageAlt: optionalText(300),
  authorName: optionalText(120),
  tags: tagList.default([]),
  status: z.enum(ARTICLE_STATUSES),
  publishedAt: z.iso.datetime().nullable().optional(),
  isFeatured: z.boolean().default(false),
  seoTitle: optionalText(300),
  seoDescription: optionalText(500),
  seoKeywords: optionalText(500),
});
export type ArticleInput = z.infer<typeof articleInputSchema>;

export const adminArticleListQuerySchema = paginationQuerySchema.extend({
  search: z.string().trim().max(100).optional(),
  status: z.enum(ARTICLE_STATUSES).optional(),
});
export type AdminArticleListQuery = z.infer<typeof adminArticleListQuerySchema>;
